import { expValEquals, expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * Monetization experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_implicit_user_monetization_experiment
 */
export const isJwmMonetizationExperimentEnabled = () =>
	!expValEquals('jwm_implicit_user_monetization_experiment', 'cohort', null);

// eslint-disable-next-line jira/ff/inline-usage
export const isVariationOnJwmMonetizationExperiment = () =>
	expValEquals('jwm_implicit_user_monetization_experiment', 'cohort', 'variation');

// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_inline_field_create
type JwmInlineFieldCreateCohort = 'not-enrolled' | 'control' | 'variation';

// eslint-disable-next-line jira/ff/inline-usage
export const isJwmInlineFieldCreateEnabled = () =>
	expValEquals<JwmInlineFieldCreateCohort>('jwm_inline_field_create', 'cohort', 'variation');

// eslint-disable-next-line jira/ff/inline-usage
export const getJwmInlineFieldCreateCohort = () =>
	expVal('jwm_inline_field_create', 'cohort', 'not-enrolled');

/**
 * JWM Automation discovery v2 (board column discoverability) experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_moon_automation_discovery_v2/setup
 */
export const isInAutomationDiscoveryOnBoardColumnsExperimentVariationCohort = () => {
	// Intentionally not firing exposure here because the user is not yet determined to be eligible.
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('jwm_moon_automation_discovery_v2');
	return expConfig.get('cohort', 'not-enrolled') === 'variation';
};

export const isAutomationDiscoveryOnBoardColumnsExperimentEnabled = () => {
	// Intentionally not firing exposure here because the user is not yet determined to be eligible.
	const [expConfig] = UNSAFE_noExposureExp('jwm_moon_automation_discovery_v2');
	return expConfig.get('cohort', 'not-enrolled') !== 'not-enrolled';
};

/**
 * JWM Template bundles experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_template_bundles_experiment
 */
// eslint-disable-next-line jira/ff/inline-usage
export const isJwmTemplateBundlesEnabled = () =>
	expValEquals('jwm_template_bundles_experiment', 'cohort', 'variation');

/**
 * JWM Link issues from board cards experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_linked_issues_on_the_board_milestone_1
 */
// eslint-disable-next-line jira/ff/inline-usage
export const isLinkedIssuesEnabledInJwmBoardCards = () =>
	expValEquals('jwm_linked_issues_on_the_board_milestone_1', 'cohort', 'variation');

/**
 * JWM Help sample project users create empty projects experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_help_sample_users_create_projects
 */
export const getJwmHelpSampleUsersCreateProjectsCtaType = () => {
	// Intentionally not firing exposure here because it is yet to be determined whether the engagement platform message has started.
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('jwm_help_sample_users_create_projects');
	return expConfig.get('ctaType', 'none');
};

/**
 * JWM Link issues from board cards experiment spotlight onboarding
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_linked_issues_on_the_board_milestone_1
 */
export const isOnboardingForLinkedIssuesEnabledInJwmBoard = () =>
	// eslint-disable-next-line jira/ff/no-preconditioning -- Need this preconditioning to make sure can independently control onboarding
	fg('jwm_linked_issues_on_the_board_onboarding') &&
	expVal('jwm_linked_issues_on_the_board_milestone_1', 'isJWMBoardLinkedIssuesEnabled', false);

/**
 * JWM Reduce noise on signup experiment
 * https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jwm_reduce_noise_on_signup
 */
// eslint-disable-next-line jira/ff/inline-usage
export const isJwmReduceNoiseOnSignupEnabled = () =>
	expValEquals('jwm_reduce_noise_on_signup', 'cohort', 'variation');
